import type { ScalarField } from './grid.ts';
import { addLayer, Grain, insertAtDepth, insertLayer, type SnowColumns } from './snowpack.ts';
import type { Topography } from './topography.ts';

/**
 * User-defined weak layers.
 *
 * The realistic way (default) is `surface`: the weak layer formed on the old snow surface before
 * the storm — surface hoar on a clear night, near-surface facets in a cold spell — and the storm
 * then buries it. Its depth is whatever snow and wind put on top, so it varies across the
 * terrain. `depth` puts it at a fixed depth inside the existing snowpack; `ground` puts it at the
 * base (depth hoar).
 */
export type WeakLayerGrain = 'SH' | 'FC' | 'DH' | 'PPgp';
export type WeakLayerPlacement = 'surface' | 'depth' | 'ground';

/** Compass sectors, in order: N, NE, E, SE, S, SW, W, NW. */
export const ASPECT_SECTORS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

export interface WeakLayerSpec {
  id: string;
  enabled: boolean;
  grain: WeakLayerGrain;
  placement: WeakLayerPlacement;
  /** For `depth`: depth of the layer's top below the existing snow surface (cm, vertical). */
  depthCm: number;
  thicknessCm: number;
  minElevation: number;
  maxElevation: number;
  /** One flag per sector in ASPECT_SECTORS order. */
  aspects: boolean[];
}

/** Typical density (kg/m³) and thickness (cm) per weak-layer type. */
export const WEAK_LAYER_DEFAULTS: Record<
  WeakLayerGrain,
  { density: number; thicknessCm: number; label: string }
> = {
  SH: { density: 150, thicknessCm: 1, label: 'Surface hoar' },
  FC: { density: 250, thicknessCm: 5, label: 'Facets' },
  DH: { density: 250, thicknessCm: 20, label: 'Depth hoar' },
  PPgp: { density: 200, thicknessCm: 2, label: 'Graupel' },
};

export const GRAIN_OF: Record<WeakLayerGrain, Grain> = {
  SH: Grain.SH,
  FC: Grain.FC,
  DH: Grain.DH,
  PPgp: Grain.PPgp,
};

/** Example layer: surface hoar on shaded aspects at mid elevations, buried by the storm. */
export const DEFAULT_WEAK_LAYERS: WeakLayerSpec[] = [
  {
    id: 'sh-1',
    enabled: true,
    grain: 'SH',
    placement: 'surface',
    depthCm: 50,
    thicknessCm: 1,
    minElevation: 600,
    maxElevation: 1400,
    aspects: [true, true, true, false, false, false, false, true],
  },
];

/** Hours before the scenario start that weak layers are stamped with. */
const SURFACE_LAYER_AGE_HOURS = 12;
const BURIED_LAYER_AGE_HOURS = 360;

/** Sector index (0 = N … 7 = NW) of a true-bearing aspect. */
export function aspectSector(aspectDeg: number): number {
  return Math.floor(((((aspectDeg + 22.5) % 360) + 360) % 360) / 45) % 8;
}

export function matchesWeakLayer(
  spec: WeakLayerSpec,
  elevation: number,
  aspectDeg: number,
): boolean {
  if (elevation < spec.minElevation || elevation > spec.maxElevation) return false;
  // Flat ground has no aspect; it only matches a layer that covers all aspects.
  if (Number.isNaN(aspectDeg)) return spec.aspects.every(Boolean);
  return spec.aspects[aspectSector(aspectDeg)] === true;
}

/**
 * Place the enabled weak layers into the columns. Only cells that already have snow get one
 * (surface hoar and facets form on snow). Returns how many cells received each layer.
 */
export function placeWeakLayers(
  cols: SnowColumns,
  specs: readonly WeakLayerSpec[],
  elevation: ScalarField,
  topo: Topography,
): Record<string, number> {
  const placed: Record<string, number> = {};
  for (const spec of specs) {
    placed[spec.id] = 0;
    if (!spec.enabled || !(spec.thicknessCm > 0)) continue;
    const { density } = WEAK_LAYER_DEFAULTS[spec.grain];
    const grain = GRAIN_OF[spec.grain];
    const mass = (spec.thicknessCm / 100) * density;
    for (let i = 0; i < cols.count.length; i++) {
      if (cols.count[i]! === 0) continue;
      if (!matchesWeakLayer(spec, elevation.data[i]!, topo.aspect.data[i]!)) continue;
      let ok: boolean;
      if (spec.placement === 'surface') {
        const before = cols.count[i]!;
        addLayer(cols, i, mass, density, grain, -SURFACE_LAYER_AGE_HOURS);
        ok = cols.count[i]! > before;
      } else if (spec.placement === 'ground') {
        ok = insertLayer(cols, i, 0, mass, density, grain, -BURIED_LAYER_AGE_HOURS);
      } else {
        ok = insertAtDepth(
          cols,
          i,
          spec.depthCm / 100,
          mass,
          density,
          grain,
          -BURIED_LAYER_AGE_HOURS,
        );
      }
      if (ok) placed[spec.id]!++;
    }
  }
  return placed;
}
